/**
 * NewsletterInline — capture email marketing réutilisable.
 *
 * Levier d'acquisition : convertit le trafic non encore prêt à entrer dans un
 * funnel en contacts opt-in (newsletter aides & primes). Branché sur
 * POST /api/leads/newsletter (l'inscription volontaire vaut consentement).
 *
 * Expérience A/B `exp_newsletter_incentive` : variante 'lead_magnet' ajoute une
 * promesse de valeur renforcée (guide). Fallback 'control' = formulaire simple.
 *
 * Utilisable en bloc Puck, dans le footer, ou en fin d'article de blog.
 */

import { useState, type FormEvent } from 'react';
import { Mail, Loader2, CheckCircle2, ArrowRight } from 'lucide-react';
import { Button } from '../ui/button';
import { cn } from '../../lib/cn';
import { captureEvent, useExperiment } from '../../lib/analytics';
import { EXPERIMENTS } from '../../config/experiments';
import { readAttribution } from '../../lib/tracking';

interface NewsletterInlineProps {
  /** Titre personnalisable (sinon valeur par défaut). */
  title?: string;
  description?: string;
  /** Variante visuelle : 'panel' (carte autonome) ou 'bare' (intégré, ex. footer sombre). */
  tone?: 'panel' | 'bare';
  className?: string;
}

type Status = 'idle' | 'loading' | 'success' | 'error';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function NewsletterInline({
  title,
  description,
  tone = 'panel',
  className,
}: NewsletterInlineProps) {
  const { variant } = useExperiment<'control' | 'lead_magnet'>(
    EXPERIMENTS.newsletterIncentive.key,
    'control',
  );
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<Status>('idle');
  const [error, setError] = useState<string | null>(null);

  const isMagnet = variant === 'lead_magnet';
  const heading =
    title ?? (isMagnet ? 'Recevez le guide des aides 2026' : 'Restez informé des nouvelles aides');
  const sub =
    description ??
    (isMagnet
      ? 'Le récap des primes CEE, MaPrimeRénov’ et coups de pouce — directement par email.'
      : 'Les évolutions des primes et aides énergie, sans spam. Désinscription en 1 clic.');

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (status === 'loading') return;
    const clean = email.trim();
    if (!EMAIL_RE.test(clean)) {
      setError('Adresse e-mail invalide');
      setStatus('error');
      return;
    }
    setStatus('loading');
    setError(null);
    try {
      const res = await fetch('/api/leads/newsletter', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        // L'inscription volontaire à la newsletter vaut consentement marketing.
        body: JSON.stringify({
          email: clean,
          consentMarketing: true,
          ...readAttribution(),
        }),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(body.error ?? `Erreur ${res.status}`);
      }
      captureEvent('newsletter_subscribe', { variant });
      setStatus('success');
      setEmail('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Réessayez dans un instant.');
      setStatus('error');
    }
  };

  const dark = tone === 'bare';

  if (status === 'success') {
    return (
      <div
        className={cn(
          'flex items-center gap-3 rounded-2xl p-5',
          dark ? 'bg-white/5 text-ivory-50' : 'border border-brand-200 bg-brand-50',
          className,
        )}
      >
        <CheckCircle2
          className={cn('h-6 w-6 shrink-0', dark ? 'text-brand-300' : 'text-brand-600')}
        />
        <div>
          <p className={cn('font-semibold', dark ? 'text-ivory-50' : 'text-brand-900')}>
            Inscription confirmée !
          </p>
          <p className={cn('text-[13px]', dark ? 'text-ivory-200' : 'text-brand-700')}>
            Vous recevrez nos prochaines actualités aides &amp; primes.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      className={cn(
        tone === 'panel' && 'rounded-2xl border border-stone-200 bg-white p-6 shadow-soft',
        className,
      )}
    >
      <div className="flex items-start gap-3">
        <span
          className={cn(
            'flex h-10 w-10 shrink-0 items-center justify-center rounded-xl',
            dark ? 'bg-white/10 text-ivory-50' : 'bg-brand-50 text-brand-700',
          )}
        >
          <Mail className="h-5 w-5" strokeWidth={1.8} />
        </span>
        <div className="min-w-0">
          <p
            className={cn(
              'font-display text-[17px] font-bold',
              dark ? 'text-ivory-50' : 'text-ink',
            )}
          >
            {heading}
          </p>
          <p
            className={cn(
              'mt-0.5 text-[13px] leading-relaxed',
              dark ? 'text-ivory-200' : 'text-stone-600',
            )}
          >
            {sub}
          </p>
        </div>
      </div>

      <form onSubmit={onSubmit} className="mt-4 flex flex-col gap-2 sm:flex-row" noValidate>
        <label className="sr-only" htmlFor="newsletter-email">
          Adresse e-mail
        </label>
        <input
          id="newsletter-email"
          type="email"
          inputMode="email"
          autoComplete="email"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            if (status === 'error') setStatus('idle');
          }}
          placeholder="vous@email.com"
          disabled={status === 'loading'}
          aria-invalid={status === 'error'}
          className={cn(
            'h-11 flex-1 rounded-xl border px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/30 disabled:opacity-60',
            dark
              ? 'border-white/15 bg-white/10 text-ivory-50 placeholder:text-ivory-300'
              : 'border-stone-300 bg-white text-ink placeholder:text-stone-400',
            status === 'error' && 'border-red-400',
          )}
        />
        <Button
          type="submit"
          variant="brand"
          size="xl"
          disabled={status === 'loading'}
          className="shrink-0 rounded-xl"
        >
          {status === 'loading' ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <>
              S'inscrire
              <ArrowRight className="h-4 w-4" strokeWidth={2.2} />
            </>
          )}
        </Button>
      </form>

      {status === 'error' && error && (
        <p className="mt-2 text-[12px] font-medium text-red-500" role="alert">
          {error}
        </p>
      )}
      <p className={cn('mt-2 text-[11px]', dark ? 'text-ivory-300' : 'text-stone-400')}>
        Sans spam, désinscription en 1 clic, aucune donnée revendue.
      </p>
    </div>
  );
}
